/**
 * DRPX Courier — Auftrags-CRUD (Supabase)
 *
 * Lese- und Schreib-Operationen auf den Tabellen `auftraege`, `angebote`
 * und `auftrag_vorlagen`. Beinhaltet auch die RPC-Wrapper für atomare
 * Operationen wie `auftrag_zurueckgeben`.
 *
 * @module lib/supabase/auftraege
 */

import { _sb } from './client.js';
import { mapAuftragFromDb, mapAngebotFromDb, mapVorlageFromDb } from './mappers.js';
import { camelToSnake } from '../mapper.js';
import { fetchRouteDistance } from '../routing.js';
import { logError } from '../monitoring.js';
import type { Auftrag, Angebot, GeoPoint, VerlaufEintrag } from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Einheitliche Fehler-Instanz, wenn der Supabase-Client nicht initialisiert
 * ist (z.B. wenn die .env-Variablen fehlen).
 */
const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

/**
 * Extrahiert eine Fehler-Nachricht aus einem `unknown`-Wert (typisch aus
 * `catch (e: unknown)`).
 */
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Input-Shape für `createAuftrag`. Erweitert `Partial<Auftrag>` um Felder,
 * die im aktuellen `Auftrag`-Interface noch nicht stehen — sie wandern bei
 * späterer Migration der Marktplatz-Module nach `types.ts`.
 */
interface AuftragCreateInput extends Partial<Auftrag> {
  firmaName?: string;
}

/**
 * Input-Shape für `updateAuftrag`. Erweitert das Create-Interface um
 * Felder, die nur nach der initialen Vergabe relevant sind (Vergabe-Daten,
 * Foto-Pfade, Verlauf).
 */
interface AuftragUpdateInput extends AuftragCreateInput {
  vergebenAn?: string;
  vergebenPreis?: number | null;
  beladungFotoPath?: string;
  beladungFotoName?: string;
  beladungFotoDatum?: string;
  lieferscheinPath?: string;
  lieferscheinName?: string;
  lieferscheinDatum?: string;
  /** Name der Person, die die Ware angenommen hat (Migration 099). */
  empfangenVon?: string;
  verlauf?: VerlaufEintrag[];
}

/**
 * Input-Shape für `createAngebot`.
 */
interface AngebotCreateInput {
  auftragId: string;
  fahrerId: string;
  fahrerName: string;
  preis: number;
  nachricht?: string;
  istFestpreis?: boolean;
  kennzeichen?: string;
  akzeptiert?: boolean;
  /**
   * Snapshot der Pflicht-Zertifikate, die der Fahrer NICHT hat (oder
   * deren Status nicht 'gueltig' ist) zum Zeitpunkt des Bietens. Wird
   * vom Frontend via `lib/zertifikat-pflicht.ts` berechnet.
   * Leeres Array oder undefined = alle Pflicht-Zertifikate vorhanden.
   * Audit 089, Migration 079.
   */
  fehlendeZertifikate?: string[];
  /**
   * Fahrzeugtyp des Bieters (Migration 126). Leer/undefined = unbekannt.
   * Nach dem Abgeben unveränderlich (Trigger `tr_angebote_fahrzeug_typ_fix`).
   */
  fahrzeugTyp?: string;
}

/**
 * Input-Shape für `updateAngebot`. Aktuell wird nur `akzeptiert` geändert.
 */
interface AngebotUpdateInput {
  akzeptiert?: boolean;
}

/**
 * Input-Shape für `createVorlage` und `updateVorlage`.
 *
 * Es gibt noch keinen `Vorlage`-Typ in `types.ts` — das Feld-Set ist hier
 * lokal definiert. Bei der späteren Vorlagen-Modul-Migration wandert das
 * nach `types.ts`.
 */
interface VorlageInput {
  name?: string;
  titel?: string;
  abhol?: string;
  ziel?: string;
  geoAbhol?: GeoPoint | null;
  geoZiel?: GeoPoint | null;
  beladeStopps?: unknown[];
  entladeStopps?: unknown[];
  fahrzeug?: string;
  ausstattung?: Record<string, boolean>;
  festpreis?: number;
  gewicht?: string;
  groesse?: string;
  notizen?: string;
  abholFirma?: string;
  abholAnsprech?: string;
  abholTel?: string;
  zielFirma?: string;
  zielAnsprech?: string;
  zielTel?: string;
  kundenRef?: string;
  wiederkehrend?: boolean;
  intervall?: string;
  wochentag?: number | null;
  monatstag?: number | null;
  uhrzeit?: string;
  naechsteErstellung?: string | null;
  letzteErstellung?: string | null;
  aktiv?: boolean;
}

/**
 * Response-Shape der RPC `auftrag_zurueckgeben`.
 */
interface ZurueckgebenResponse {
  success?: boolean;
  neuer_status?: string;
  error?: string;
}

// ════════════════════════════════════════════════════════════════════
// Update-Whitelists
// ════════════════════════════════════════════════════════════════════

/**
 * Whitelist der Felder, die per `updateAuftrag` geändert werden dürfen.
 * Verhindert versehentliches Überschreiben von `id`, `firma_id`, `nr`,
 * `created_at`, `tracking_token` etc. (RLS in der DB ist die zweite
 * Schutzschicht).
 */
const AUFTRAG_UPDATE_WHITELIST = [
  'status',
  'titel',
  'abhol',
  'ziel',
  'kundenRef',
  'geoAbhol',
  'geoZiel',
  'beladeStopps',
  'entladeStopps',
  'gewicht',
  'groesse',
  'datum',
  'zeit',
  'entladeDatum',
  'entladeZeit',
  'anzahl',
  'volumen',
  'packstuecke',
  'abholFirma',
  'abholAnsprech',
  'abholTel',
  'zielFirma',
  'zielAnsprech',
  'zielTel',
  'festpreis',
  'notizen',
  'fahrzeug',
  'ausstattung',
  'vergebenAn',
  'vergebenPreis',
  'beladungFotoPath',
  'beladungFotoName',
  'beladungFotoDatum',
  'lieferscheinPath',
  'lieferscheinName',
  'lieferscheinDatum',
  'empfangenVon',
  'verlauf',
] as const satisfies readonly (keyof AuftragUpdateInput)[];

/**
 * Whitelist der Felder, die per `updateVorlage` geändert werden dürfen.
 * Wird primär für die Wiederholungs-Konfiguration verwendet (Intervall,
 * aktiv-Flag, nächste Erstellung nach Cron-Lauf).
 */
const VORLAGE_UPDATE_WHITELIST = [
  'name',
  'wiederkehrend',
  'intervall',
  'wochentag',
  'monatstag',
  'uhrzeit',
  'naechsteErstellung',
  'letzteErstellung',
  'aktiv',
] as const satisfies readonly (keyof VorlageInput)[];

// ════════════════════════════════════════════════════════════════════
// AUFTRÄGE
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Aufträge (max 500) und verknüpft sie mit den zugehörigen
 * Angeboten (max 2000). Auftrag und Angebote werden in zwei separaten
 * Queries geladen und im Speicher zusammengefügt.
 *
 * Für Fahrer wird zusätzlich die Marktplatz-View gelesen (ohne die
 * Kontakt-Felder abhol_ansprech/abhol_tel/ziel_ansprech/ziel_tel),
 * und mit den eigenen zugewiesenen Aufträgen (mit vollen Kontaktdaten
 * aus der direkten Tabelle) zusammengeführt. Eigene Einträge haben
 * Priorität bei Duplikaten.
 *
 * @param isFahrer - Wenn true, wird die Marktplatz-View für den Teaser
 *                   gelesen und mit den zugewiesenen Aufträgen kombiniert.
 *                   Für Firma und Admin direkt aus der auftraege-Tabelle.
 *                   Default false (alte Signatur-kompatibel).
 */
export async function getAuftraege(
  isFahrer: boolean = false,
): Promise<{
  data: Auftrag[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    // ─── Aufträge laden ───────────────────────────────────────────
    // Fahrer: zwei Quellen kombinieren (Marktplatz-View ohne Kontakt-
    // Felder + eigene Aufträge aus direkter Tabelle mit vollen Feldern).
    // Firma/Admin: direkt aus auftraege.
    const auftraegeMap: Record<string, Auftrag> = {};

    if (isFahrer) {
      // (1) Marktplatz-Teaser aus View (ohne Kontakt-Felder)
      const viewRes = await client
        .from('auftraege_marktplatz')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (viewRes.error) {
        console.error('getAuftraege (marktplatz view):', viewRes.error.message);
      }
      const viewRows = (viewRes.data || []) as Array<Record<string, unknown>>;
      viewRows.forEach((row) => {
        const mapped = mapAuftragFromDb(row);
        if (mapped) auftraegeMap[mapped.id] = mapped;
      });

      // (2) Eigene Aufträge (Fahrer hat Angebot oder ist zugewiesen)
      //     mit VOLLEN Feldern (Kontakt-Felder) aus direkter Tabelle.
      //     Die RLS-Policy auftraege_select_berechtigt filtert auf
      //     has_angebot_on_auftrag() / vergeben_an.
      const ownRes = await client
        .from('auftraege')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (ownRes.error) {
        console.error('getAuftraege (eigene):', ownRes.error.message);
      }
      const ownRows = (ownRes.data || []) as Array<Record<string, unknown>>;
      // Eigene haben Priorität bei Duplikaten (enthalten die Kontakt-Felder).
      ownRows.forEach((row) => {
        const mapped = mapAuftragFromDb(row);
        if (mapped) auftraegeMap[mapped.id] = mapped;
      });
    } else {
      // Firma/Admin: direkt aus auftraege — RLS liefert nur erlaubte Zeilen.
      const r = await client
        .from('auftraege')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (r.error) {
        console.error('getAuftraege:', r.error.message);
        return { data: [] };
      }
      const rows = (r.data || []) as Array<Record<string, unknown>>;
      rows.forEach((row) => {
        const mapped = mapAuftragFromDb(row);
        if (mapped) auftraegeMap[mapped.id] = mapped;
      });
    }

    const auftraege = Object.values(auftraegeMap);

    // ─── Angebote separat laden ────────────────────────────────────
    // Gelesen wird aus der View `angebote_mit_bonitaet` (Migration 115),
    // NICHT aus der Tabelle: Die View hängt `fahrer_bonitaet_geprueft`
    // des Bieters an (LEFT JOIN auf profiles). Ein Client-Lookup ist
    // nicht möglich — die RLS auf profiles erlaubt nur das eigene Profil.
    //
    // Die View ist eine Definer-View und bildet die Policy
    // `angebote_select_berechtigt` in ihrer WHERE-Klausel exakt nach:
    // eigene Angebote (fahrer_id) oder Angebote auf eigene Aufträge
    // (firma_id) oder Admin. Die Filterung ist also unverändert.
    //
    // Schreibzugriffe (insert/update/delete weiter unten) laufen
    // unverändert gegen die Tabelle `angebote`.
    //
    // BEKANNTE GRENZE: Realtime publiziert aus der TABELLE, nicht aus der
    // View. Ein live eintreffendes Angebot hat daher kein
    // `fahrerBonitaetGeprueft` und zeigt bis zum nächsten vollen Laden
    // kein Badge. BonitaetBadge rendert bei undefined nichts — kein Fehler.
    const ar = await client.from('angebote_mit_bonitaet').select('*').limit(2000);
    if (ar.data) {
      auftraege.forEach((a) => {
        // Defensive Initialisierung: Mapper liefert zwar bereits `angebote: []`,
        // aber im Typ ist es optional — explizit setzen sichert den Push unten.
        if (!a.angebote) a.angebote = [];
      });
      const angeboteRows = ar.data as Array<Record<string, unknown>>;
      angeboteRows.forEach((raw) => {
        const ang = mapAngebotFromDb(raw);
        if (!ang) return;
        const target = auftraegeMap[ang.auftragId];
        if (target && target.angebote) {
          target.angebote.push(ang);
        }
      });
    }
    return { data: auftraege };
  } catch (e: unknown) {
    console.error('getAuftraege:', e);
    return { data: [], error: e };
  }
}

/**
 * Holt asynchron die echte Straßen-Distanz via OSRM und schreibt sie
 * in den neu erstellten Auftrag. Fire-and-forget — Fehler werden nur
 * geloggt, nie geworfen. Blockiert NICHT die Auftrags-Erstellung.
 *
 * Bedingungen für den Fetch:
 *  - Beide Geo-Punkte (geo_abhol + geo_ziel) vorhanden
 *  - OSRM liefert gültigen Wert
 * Sonst: tour_distanz_km bleibt NULL, MarktplatzCard nutzt Fallback.
 *
 * Audit 087 (17.05.2026, Befund #6): Fehler gehen zusätzlich an
 * monitoring_events via logError(), damit systematische OSRM-/RLS-
 * Probleme im Admin-Dashboard sichtbar werden (vorher nur console.warn).
 */
function fetchAndStoreTourDistanz(
  auftragId: string,
  geoAbhol: GeoPoint | null | undefined,
  geoZiel: GeoPoint | null | undefined,
): void {
  if (!_sb) return;
  if (!geoAbhol || !geoZiel) return;
  const client = _sb;
  void fetchRouteDistance(geoAbhol, geoZiel)
    .then(function (km) {
      if (km === null) return; // API-Fehler/Timeout — stiller Fallback
      return client
        .from('auftraege')
        .update({ tour_distanz_km: km })
        .eq('id', auftragId);
    })
    .then(function (r) {
      if (r && r.error) {
        console.warn('tour_distanz_km update failed:', r.error.message);
        logError(r.error, 'fetchAndStoreTourDistanz/update');
      }
    })
    .catch(function (e: unknown) {
      console.warn('fetchAndStoreTourDistanz:', e);
      logError(e, 'fetchAndStoreTourDistanz/catch');
    });
}

/**
 * Erstellt einen neuen Auftrag. Auftragsnummer wird per DB-Trigger generiert.
 */
export async function createAuftrag(
  a: AuftragCreateInput,
): Promise<{ data: Auftrag | null; error?: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      firma_id: a.firmaId,
      firma_name: a.firmaName,
      titel: a.titel,
      abhol: a.abhol,
      ziel: a.ziel,
      kunden_ref: a.kundenRef || '',
      gewicht: a.gewicht || '',
      groesse: a.groesse || '',
      datum: a.datum || null,
      zeit: a.zeit || '',
      festpreis: a.festpreis || 0,
      notizen: a.notizen || '',
      fahrzeug: a.fahrzeug || 'transporter',
      ausstattung: a.ausstattung || {},
      status: 'offen',
      belade_stopps: a.beladeStopps || [],
      entlade_stopps: a.entladeStopps || [],
      entlade_datum: a.entladeDatum || null,
      entlade_zeit: a.entladeZeit || '',
      anzahl: a.anzahl || '',
      volumen: a.volumen || '',
      packstuecke: a.packstuecke || [],
      abhol_firma: a.abholFirma || '',
      abhol_ansprech: a.abholAnsprech || '',
      abhol_tel: a.abholTel || '',
      ziel_firma: a.zielFirma || '',
      ziel_ansprech: a.zielAnsprech || '',
      ziel_tel: a.zielTel || '',
      geo_abhol: a.geoAbhol || null,
      geo_ziel: a.geoZiel || null,
    };
    const r = await client.from('auftraege').insert(d).select().single();
    if (r.error) {
      console.error('createAuftrag:', r.error.message);
      return { data: null, error: r.error };
    }
    const mapped = mapAuftragFromDb(r.data as Record<string, unknown>);
    if (!mapped) {
      return { data: null, error: new Error('Mapping fehlgeschlagen') };
    }
    // Async: Tour-Distanz via OSRM holen und updaten. Blockiert NICHT die
    // Rückgabe an den Caller — Auftrag ist sofort verfügbar.
    fetchAndStoreTourDistanz(mapped.id, a.geoAbhol, a.geoZiel);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Aktualisiert einen Auftrag (Status, Verlauf, etc.).
 *
 * Akzeptiert nur Felder aus `AUFTRAG_UPDATE_WHITELIST` und konvertiert
 * camelCase → snake_case automatisch via `camelToSnake`. Felder außerhalb
 * der Whitelist werden stillschweigend ignoriert.
 */
export async function updateAuftrag(
  id: string,
  updates: AuftragUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of AUFTRAG_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        d[camelToSnake(key)] = updates[key];
      }
    }
    const r = await client.from('auftraege').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht einen Auftrag.
 */
export async function deleteAuftrag(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('auftraege').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// ANGEBOTE
// ════════════════════════════════════════════════════════════════════

/**
 * Legt ein neues Angebot eines Fahrers für einen Auftrag an.
 * Der Status des zugehörigen Auftrags wird automatisch auf "angebote"
 * gesetzt (DB-Trigger aus Migration 021).
 */
export async function createAngebot(
  a: AngebotCreateInput,
): Promise<{ data: Angebot | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      auftrag_id: a.auftragId,
      fahrer_id: a.fahrerId,
      fahrer_name: a.fahrerName,
      preis: a.preis,
      nachricht: a.nachricht || '',
      ist_festpreis: !!a.istFestpreis,
      kennzeichen: a.kennzeichen || '',
      akzeptiert: !!a.akzeptiert,
      // Audit 089, Migration 079: Snapshot der fehlenden Pflicht-Zertifikate
      // zum Zeitpunkt des Bietens. Wird vom Frontend via
      // lib/zertifikat-pflicht.ts berechnet. Leeres Array bei null/undefined.
      fehlende_zertifikate: Array.isArray(a.fehlendeZertifikate)
        ? a.fehlendeZertifikate
        : [],
      // Migration 126: Fahrzeugtyp des Bieters ('' = unbekannt)
      fahrzeug_typ: (a.fahrzeugTyp || '').trim().slice(0, 60),
    };
    const r = await client.from('angebote').insert(d).select().single();

    // ── Migration 072: Postgres-Constraint-Fehler in strukturierte
    //    ApiError mit i18n-Key übersetzen, damit das Frontend dem User
    //    eine freundliche Toast-Meldung zeigen kann statt eines
    //    Postgres-Fehlertexts.
    if (r.error) {
      // Supabase liefert PostgrestError mit code-Feld (z.B. '23505').
      const pgCode = (r.error as { code?: string }).code;
      const pgMsg = (r.error as { message?: string }).message ?? '';

      // 23505 = unique_violation → unser UNIQUE(auftrag_id, fahrer_id).
      // Tritt auf, wenn der Fahrer in 2 Tabs gleichzeitig bietet oder
      // ein laufendes Gebot doppelt absendet.
      if (pgCode === '23505' && pgMsg.includes('angebote_one_per_fahrer_auftrag')) {
        return { data: null, error: { code: 'hcAngebotBereitsAbgegeben' } };
      }

      // 23514 = check_violation → preis <= 0 oder > 1 Mio.
      // Tritt nur auf bei Frontend-Bypass (sollte nie regulär passieren).
      if (pgCode === '23514' && pgMsg.includes('angebote_preis_plausibel')) {
        return { data: null, error: { code: 'hcAngebotPreisUnplausibel' } };
      }

      // 42501 = RLS-Violation oder allgemein insufficient_privilege.
      // Häufigster Auslöser hier (Migration 072): Auftrag-Status ist
      // nicht mehr 'offen'/'angebote', weil zwischenzeitlich vergeben.
      if (pgCode === '42501') {
        return { data: null, error: { code: 'hcAngebotNichtMoeglich' } };
      }
    }

    return {
      data: r.data ? mapAngebotFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Aktualisiert ein Angebot. Aktuell wird praktisch nur das Feld
 * `akzeptiert` verwendet, wenn die Firma das Angebot annimmt.
 */
export async function updateAngebot(
  id: string,
  updates: AngebotUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    if (updates.akzeptiert !== undefined) d.akzeptiert = updates.akzeptiert;
    const r = await client.from('angebote').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht ein einzelnes Angebot. Der Auftrags-Status wird automatisch
 * zurückgesetzt (auf "offen"), wenn es das letzte Angebot war (DB-Trigger
 * aus Migration 021).
 */
export async function deleteAngebot(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('angebote').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht alle Angebote zu einem Auftrag. Wird z.B. beim Stornieren oder
 * beim Löschen eines Auftrags aufgerufen, um Waisen-Datensätze zu vermeiden.
 */
export async function deleteAngeboteByAuftrag(
  auftragId: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('angebote').delete().eq('auftrag_id', auftragId);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// AUFTRAG-VORLAGEN
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Auftrags-Vorlagen einer Firma. Vorlagen sind wiederverwendbare
 * Auftrags-Templates, optional auch als wiederkehrende Aufträge (täglich,
 * wöchentlich, monatlich) — dann erzeugt ein Cron-Job automatisch neue
 * Aufträge.
 */
export async function getVorlagen(
  firmaId: string,
): Promise<{ data: Array<Record<string, unknown>>; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_vorlagen')
      .select('*')
      .eq('firma_id', firmaId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapVorlageFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt eine neue Auftrags-Vorlage an. Kann als einmalige Vorlage oder
 * als wiederkehrender Auftrag konfiguriert werden (`wiederkehrend=true` +
 * `intervall` + `wochentag/monatstag` + `uhrzeit`).
 */
export async function createVorlage(
  firmaId: string,
  v: VorlageInput,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      firma_id: firmaId,
      name: v.name || '',
      titel: v.titel || '',
      abhol: v.abhol || '',
      ziel: v.ziel || '',
      geo_abhol: v.geoAbhol || null,
      geo_ziel: v.geoZiel || null,
      belade_stopps: v.beladeStopps || [],
      entlade_stopps: v.entladeStopps || [],
      fahrzeug: v.fahrzeug || 'transporter',
      ausstattung: v.ausstattung || {},
      festpreis: v.festpreis || 0,
      gewicht: v.gewicht || '',
      groesse: v.groesse || '',
      notizen: v.notizen || '',
      abhol_firma: v.abholFirma || '',
      abhol_ansprech: v.abholAnsprech || '',
      abhol_tel: v.abholTel || '',
      ziel_firma: v.zielFirma || '',
      ziel_ansprech: v.zielAnsprech || '',
      ziel_tel: v.zielTel || '',
      kunden_ref: v.kundenRef || '',
      wiederkehrend: !!v.wiederkehrend,
      intervall: v.intervall || '',
      wochentag: v.wochentag ?? null,
      monatstag: v.monatstag ?? null,
      uhrzeit: v.uhrzeit || '08:00',
      naechste_erstellung: v.naechsteErstellung || null,
      letzte_erstellung: v.letzteErstellung || null,
      aktiv: v.aktiv !== false,
    };
    const r = await client.from('auftrag_vorlagen').insert(d).select().single();
    return {
      data: r.data ? mapVorlageFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Aktualisiert eine Vorlage. Wird primär für Änderungen an der
 * Wiederholungs-Konfiguration verwendet (Intervall, aktiv-Flag, nächste
 * Erstellung nach Cron-Lauf).
 */
export async function updateVorlage(
  id: string,
  updates: VorlageInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of VORLAGE_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        d[camelToSnake(key)] = updates[key];
      }
    }
    const r = await client.from('auftrag_vorlagen').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht eine Auftrags-Vorlage unwiderruflich. Bereits erzeugte Aufträge
 * aus dieser Vorlage bleiben unangetastet.
 */
export async function deleteVorlage(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('auftrag_vorlagen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// RPC
// ════════════════════════════════════════════════════════════════════

/**
 * Gibt einen zugewiesenen Auftrag zurück (Fahrer → Marktplatz). Atomar
 * via DB-RPC: löscht das Angebot, setzt `vergeben_an` und `status` zurück.
 */
export async function zurueckgebenAuftrag(
  auftragId: string,
): Promise<{ data?: ZurueckgebenResponse; error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('auftrag_zurueckgeben', { p_auftrag_id: auftragId });
    if (r.error) return { error: r.error.message };
    const data = r.data as ZurueckgebenResponse | null;
    if (data && data.error) return { error: data.error };
    return { data: data ?? undefined };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Markiert einen Auftrag als von der Firma "gesehen" (firma_seen_at = now).
 *
 * Wird beim Aufklappen einer Auftrags-Card aufgerufen. Frontend nutzt
 * `firma_seen_at` zur Berechnung "wie viele Angebote sind seit dem letzten
 * Sichten neu hinzugekommen" — analog zu E-Mail "ungelesen".
 *
 * Sicherheit: RPC ist SECURITY DEFINER und prüft intern `firma_id = auth.uid()`.
 * Bei fremdem Auftrag stilles No-Op (kein Error) → defensive UX, fire-and-forget.
 *
 * @param auftragId Auftrags-UUID
 * @returns void Promise — Errors werden geloggt, nicht gethrown
 */
export async function markAuftragSeen(auftragId: string): Promise<void> {
  if (!_sb) return;
  const client = _sb;
  try {
    const r = await client.rpc('mark_auftrag_seen', { p_auftrag_id: auftragId });
    if (r.error) {
      // Stilles Logging — kein Toast, weil das ein Background-Update ist.
      console.warn('mark_auftrag_seen failed:', r.error.message);
    }
  } catch (e: unknown) {
    console.warn('mark_auftrag_seen exception:', errMsg(e));
  }
}

/**
 * Response-Shape für die akzeptiere_angebot RPC.
 */
export interface AkzeptiereAngebotResponse {
  success: boolean;
  auftrag_id?: string;
  angebot_id?: string;
  fahrer_id?: string;
  preis?: number;
  abgelehnt_anzahl?: number;
  error?: string;
}

/**
 * Firma akzeptiert ein Angebot — atomar via DB-RPC.
 *
 * Behebt Finding F1 (validate_status_transition blockiert direkten UPDATE)
 * und F2 (Race-Condition bei parallelen Tab-Vergaben), indem es alle
 * Schritte (Angebot akzeptieren, Auftrag auf 'vergeben' setzen, andere
 * Angebote löschen) in einer DB-Transaktion ausführt.
 *
 * Auth: Die RPC prüft intern, ob der Caller die Firma des Auftrags ist.
 */
export async function akzeptiereAngebot(
  auftragId: string,
  angebotId: string,
): Promise<{ data?: AkzeptiereAngebotResponse; error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('akzeptiere_angebot', {
      p_auftrag_id: auftragId,
      p_angebot_id: angebotId,
    });
    if (r.error) return { error: r.error.message };
    const data = r.data as AkzeptiereAngebotResponse | null;
    if (data && data.error) return { error: data.error };
    return { data: data ?? undefined };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}
